import {
  Box,
  Button,
  Center,
  Flex,
  Image,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  Spinner,
  Stack,
  Text,
  useToast,
  VStack,
} from '@chakra-ui/react';
import type {
  VoucherEligibilityItem,
  VoucherGetByCodeResponse,
} from '@diamond/shared/types';
import { showToast } from '@diamond/shared/ui';
import { getErrorMessage } from '@diamond/shared/utils';
import {
  useCartStore,
  useCartTotal,
  useVoucherByCode,
  useVoucherCombinationRules,
  useVoucherList,
  useVoucherStore,
} from '@diamond/sol-buyer-context';
import { useEffect, useRef, useState } from 'react';
import { useDebounceCallback } from 'usehooks-ts';

import VoucherListSection from '../../voucher/VoucherListSection';
import CartVoucherHeader, {
  type CartVoucherHeaderHandle,
} from './CartVoucherHeader';
import VoucherDetailsContent from './VoucherDetailsContent';

interface CartVoucherModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type ViewMode = 'list' | 'searched' | 'details';

const mergeVoucherDetails = (
  summary: VoucherEligibilityItem | VoucherGetByCodeResponse,
  detail: VoucherGetByCodeResponse
): VoucherEligibilityItem | VoucherGetByCodeResponse => {
  const messageRules =
    'message_rule_products' in summary ? summary.message_rule_products : [];

  const ineligibilityReasons =
    'ineligibility_reasons' in summary
      ? summary.ineligibility_reasons
      : undefined;

  return {
    ...summary,
    ...detail,
    ...(messageRules ? { message_rule_products: messageRules } : {}),
    ...(ineligibilityReasons
      ? { ineligibility_reasons: ineligibilityReasons }
      : {}),
  };
};

const checkVoucherDisabledByCombination = (
  searchedVoucher: VoucherGetByCodeResponse,
  appliedVoucherCodes: string[],
  eligibilityVouchers: VoucherEligibilityItem[]
): boolean => {
  const searchedCode = searchedVoucher.code.trim();
  if (!searchedCode) {
    return false;
  }

  const isApplied = appliedVoucherCodes.includes(searchedCode);
  const searchedAllowOther =
    'allow_other_vouchers' in searchedVoucher
      ? Boolean(searchedVoucher.allow_other_vouchers)
      : undefined;

  const allowOtherByCode: Record<string, boolean> = {};

  eligibilityVouchers.forEach((voucher) => {
    const code = voucher.code?.trim();
    if (code && 'allow_other_vouchers' in voucher) {
      allowOtherByCode[code] = Boolean(voucher.allow_other_vouchers);
    }
  });

  if ('allow_other_vouchers' in searchedVoucher) {
    allowOtherByCode[searchedCode] = Boolean(
      searchedVoucher.allow_other_vouchers
    );
  }

  const getAllowOtherForCode = (code: string): boolean | undefined => {
    const normalized = code.trim();
    return normalized ? allowOtherByCode[normalized] : undefined;
  };

  const nonCombinableAppliedCodes: string[] = [];
  const combinableAppliedCodes: string[] = [];

  appliedVoucherCodes.forEach((rawCode) => {
    const code = rawCode.trim();
    if (!code) {
      return;
    }

    const allowOther = getAllowOtherForCode(code);

    if (allowOther === false) {
      nonCombinableAppliedCodes.push(code);
    } else if (allowOther === true) {
      combinableAppliedCodes.push(code);
    }
  });

  const hasNonCombinableApplied = nonCombinableAppliedCodes.length > 0;
  const hasCombinableApplied = combinableAppliedCodes.length > 0;

  if (hasNonCombinableApplied) {
    const isOneOfNonCombinable =
      nonCombinableAppliedCodes.includes(searchedCode);
    if (isOneOfNonCombinable) {
      return false;
    }

    return true;
  }

  if (hasCombinableApplied) {
    if (searchedAllowOther === false && !isApplied) {
      return true;
    }

    return false;
  }

  return false;
};

export const CartVoucherModal = ({
  isOpen,
  onClose,
}: CartVoucherModalProps) => {
  const toast = useToast();
  const headerRef = useRef<CartVoucherHeaderHandle>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [searchedVoucher, setSearchedVoucher] =
    useState<VoucherGetByCodeResponse | null>(null);
  const [selectedVoucherForDetails, setSelectedVoucherForDetails] = useState<
    VoucherEligibilityItem | VoucherGetByCodeResponse | null
  >(null);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [detailFetchError, setDetailFetchError] = useState<string | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [isSyncingCart, setIsSyncingCart] = useState(false);

  const cartItems = useCartStore((state) => state.cartItems);

  const addVoucher = useVoucherStore((state) => state.addVoucher);
  const removeVoucher = useVoucherStore((state) => state.removeVoucher);
  const isVoucherApplied = useVoucherStore((state) => state.isVoucherApplied);
  const addPendingVoucher = useVoucherStore((state) => state.addPendingVoucher);
  const isPendingVoucher = useVoucherStore((state) => state.isPendingVoucher);
  const clearPendingVouchers = useVoucherStore(
    (state) => state.clearPendingVouchers
  );

  const { isFetching: isCartTotalFetching } = useCartTotal();

  const {
    data: voucherList,
    isLoading: isLoadingList,
    isError: isErrorList,
    error: errorList,
    refetch: refetchList,
  } = useVoucherList(isOpen);

  const { fetchWithCache } = useVoucherByCode();

  const appliedVouchers = useVoucherStore((state) => state.appliedVouchers);
  // Extract codes for backward compatibility with combination rules
  const appliedVoucherCodes = appliedVouchers.map((v) => v.code);

  const eligibilityVouchers: VoucherEligibilityItem[] =
    voucherList?.pages?.flatMap((page) => page.data ?? []) ?? [];

  const { isVoucherDisabledByCombination, getCombinationDisabledReason } =
    useVoucherCombinationRules({
      eligibilityVouchers,
      searchedVoucher: searchedVoucher ?? undefined,
      selectedVoucherForDetails: selectedVoucherForDetails ?? undefined,
      appliedVoucherCodes,
      isVoucherApplied,
    });

  const handleSearch = async (voucherCode: string) => {
    if (!voucherCode.trim()) {
      showToast(
        toast,
        'warning',
        'Kode voucher kosong - Silakan masukkan kode voucher',
        undefined,
        'bottom',
        3000
      );
      return;
    }

    setIsSearching(true);
    try {
      const result = await fetchWithCache(voucherCode.trim());
      const code = result.code.trim();

      setSearchedVoucher(result);
      setSelectedVoucherForDetails(result);
      setViewMode('details');

      const isDisabledByCombination = checkVoucherDisabledByCombination(
        result,
        appliedVoucherCodes,
        eligibilityVouchers
      );

      if (isDisabledByCombination) {
        showToast(
          toast,
          'warning',
          'Voucher tidak bisa digabung dengan voucher lain',
          undefined,
          'bottom',
          3000
        );
        return;
      }

      if (result.is_eligible) {
        addPendingVoucher(code);
        addVoucher(result.id, code);

        showToast(
          toast,
          'success',
          'Voucher berhasil diterapkan',
          undefined,
          'bottom',
          3000
        );
      } else {
        const warningMessage =
          result.warning_messages?.[0]?.messages ||
          'Voucher tidak dapat digunakan saat ini';
        showToast(toast, 'warning', warningMessage, undefined, 'bottom', 3000);
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Voucher tidak ditemukan atau tidak dapat digunakan';
      showToast(toast, 'error', errorMessage, undefined, 'bottom', 3000);
    } finally {
      setIsSearching(false);
    }
  };

  const handleCardClick = async (
    voucher: VoucherEligibilityItem | VoucherGetByCodeResponse
  ) => {
    setDetailFetchError(null);
    setIsLoadingDetails(true);

    try {
      const fullVoucherDetails = await fetchWithCache(voucher.code.trim());
      const enrichedVoucher = mergeVoucherDetails(voucher, fullVoucherDetails);
      setSelectedVoucherForDetails(enrichedVoucher);
      setViewMode('details');
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Gagal memuat detail voucher';
      setDetailFetchError(errorMessage);
      setSelectedVoucherForDetails(voucher);
      setViewMode('details');
    } finally {
      setIsLoadingDetails(false);
    }
  };

  const handleBackFromDetails = () => {
    setViewMode('list');
    setSelectedVoucherForDetails(null);
    setDetailFetchError(null);
    // Reset search field when going back to list
    headerRef.current?.resetSearchField();
  };

  const handleRetryDetailsFetch = async () => {
    if (!selectedVoucherForDetails) return;
    await handleCardClick(selectedVoucherForDetails);
  };

  const handleApplyFromDetails = () => {
    if (!selectedVoucherForDetails) return;

    const code = selectedVoucherForDetails.code.trim();
    if (!code) return;

    if (isPendingVoucher(code)) return;

    if (isVoucherDisabledByCombination(code)) {
      const reason = getCombinationDisabledReason(code);
      showToast(
        toast,
        'warning',
        reason ??
          'Voucher ini tidak dapat digunakan bersamaan dengan voucher lain yang sedang digunakan.',
        undefined,
        'bottom',
        3000
      );
      return;
    }

    addPendingVoucher(code);

    if (isVoucherApplied(code)) {
      removeVoucher(code);
      showToast(
        toast,
        'success',
        'Voucher berhasil dihapus',
        undefined,
        'bottom',
        3000
      );
    } else {
      const voucherId = selectedVoucherForDetails.id;
      addVoucher(voucherId, code);
      showToast(
        toast,
        'success',
        'Voucher berhasil diterapkan',
        undefined,
        'bottom',
        3000
      );
    }
  };

  const handleApplyVoucher = (voucherId: string, voucherCode: string) => {
    const id = voucherId.trim();
    const code = voucherCode.trim();
    if (!id || !code) return;

    if (isPendingVoucher(code)) return;

    if (isVoucherDisabledByCombination(code)) {
      const reason = getCombinationDisabledReason(code);
      showToast(
        toast,
        'warning',
        reason ??
          'Voucher ini tidak dapat digunakan bersamaan dengan voucher lain yang sedang digunakan.',
        undefined,
        'bottom',
        3000
      );
      return;
    }

    addPendingVoucher(code);

    if (isVoucherApplied(code)) {
      removeVoucher(code);
      showToast(
        toast,
        'success',
        'Voucher Berhasil Dihapus',
        undefined,
        'bottom',
        3000
      );
    } else {
      addVoucher(id, code);
      showToast(
        toast,
        'success',
        'Voucher Berhasil Digunakan',
        undefined,
        'bottom',
        3000
      );
    }

    if (viewMode === 'searched') {
      handleBackToList();
    }
  };

  const debouncedHandleApply = useDebounceCallback(handleApplyVoucher, 300);

  useEffect(() => {
    if (!isCartTotalFetching) {
      clearPendingVouchers();
    }
  }, [isCartTotalFetching, clearPendingVouchers]);

  useEffect(() => {
    clearPendingVouchers();
  }, [appliedVouchers, clearPendingVouchers]);

  const handleBackToList = () => {
    setViewMode('list');
    setSearchedVoucher(null);
    headerRef.current?.resetSearchField();
  };

  const handleClose = () => {
    setViewMode('list');
    setSearchedVoucher(null);
    setSelectedVoucherForDetails(null);
    headerRef.current?.resetSearchField();
    onClose();
  };

  const handleCartSyncStart = () => {
    setIsSyncingCart(true);
  };

  const handleCartSyncComplete = () => {
    setIsSyncingCart(false);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size="lg"
      scrollBehavior="inside"
      isCentered
    >
      <ModalOverlay />
      <ModalContent borderRadius="8px" overflow="hidden" maxW="520px">
        <ModalBody p={0} position="relative">
          {isSyncingCart && (
            <Flex
              position="absolute"
              inset={0}
              bg="whiteAlpha.800"
              zIndex={10}
              justify="center"
              align="center"
              direction="column"
              gap={4}
            >
              <Spinner size="lg" color="#007AFF" />
              <Text fontSize="md" fontWeight="medium">
                Menambahkan Produk ke Keranjang
              </Text>
            </Flex>
          )}
          {viewMode === 'details' ? (
            selectedVoucherForDetails && (
              <VoucherDetailsContent
                voucher={selectedVoucherForDetails}
                isApplied={isVoucherApplied(selectedVoucherForDetails.code)}
                isProcessing={isPendingVoucher(selectedVoucherForDetails.code)}
                isEligible={
                  'is_eligible' in selectedVoucherForDetails
                    ? selectedVoucherForDetails.is_eligible
                    : true
                }
                isLoadingDetails={isLoadingDetails}
                detailFetchError={detailFetchError}
                onApply={handleApplyFromDetails}
                onBack={handleBackFromDetails}
                onClose={handleClose}
                onRetry={handleRetryDetailsFetch}
                isDisabled={isVoucherDisabledByCombination(
                  selectedVoucherForDetails.code
                )}
                disabledReason={getCombinationDisabledReason(
                  selectedVoucherForDetails.code
                )}
              />
            )
          ) : (
            <>
              <CartVoucherHeader
                onSearch={handleSearch}
                isSearching={isSearching}
                onClose={handleClose}
                headerRef={headerRef}
              />

              <Box bg="#F7F7FB" px="16px" py="20px" minH="300px">
                {viewMode === 'list' ? (
                  <>
                    {cartItems.length === 0 && (
                      <Center py={8} px={4}>
                        <VStack spacing={4} textAlign="center">
                          <Image
                            src="/images/voucher/voucher-select-product.png"
                            alt="Select product to get vouchers"
                            objectFit="cover"
                            display="block"
                            marginX="auto"
                            height={{ base: '120px', md: '160px' }}
                            width={{ base: '120px', md: '160px' }}
                          />
                          <Stack spacing={1}>
                            <Text as="b" fontSize={{ base: 'md', md: 'lg' }}>
                              Pilih produk terlebih dahulu
                            </Text>
                            <Text
                              fontSize={{ base: 'sm', md: 'md' }}
                              color="gray.500"
                            >
                              Pilih Produk di Keranjang untuk melihat voucher
                              yang tersedia
                            </Text>
                          </Stack>
                        </VStack>
                      </Center>
                    )}
                    {isLoadingList && (
                      <Flex justify="center" py="40px">
                        <Spinner size="lg" color="#007AFF" />
                      </Flex>
                    )}
                    {isErrorList && !isLoadingList && (
                      <Center py={8} px={4}>
                        <VStack spacing={4} textAlign="center">
                          <Image
                            src="/voucher/voucher_error.png"
                            alt="Error loading voucher"
                            objectFit="cover"
                            display="block"
                            marginX="auto"
                            height={{ base: '120px', md: '160px' }}
                            width={{ base: '120px', md: '160px' }}
                          />
                          <Stack spacing={1}>
                            <Text as="b" fontSize={{ base: 'md', md: 'lg' }}>
                              Gagal memuat voucher
                            </Text>
                            <Text
                              fontSize={{ base: 'sm', md: 'md' }}
                              color="gray.500"
                            >
                              {getErrorMessage(errorList)}
                            </Text>
                          </Stack>
                          <Button variant="solid" onClick={() => refetchList()}>
                            Coba lagi
                          </Button>
                        </VStack>
                      </Center>
                    )}
                    {!isLoadingList && !isErrorList && voucherList && (
                      <VoucherListSection
                        vouchers={
                          voucherList.pages?.flatMap((page) => page.data) ?? []
                        }
                        statusFilter="all"
                        searchTerm=""
                        onVoucherApplied={(voucher) =>
                          voucher &&
                          debouncedHandleApply(voucher.id, voucher.code)
                        }
                        onVoucherClick={handleCardClick}
                        isVoucherDisabledByCombination={(voucher) =>
                          isVoucherDisabledByCombination(voucher.code)
                        }
                        onCartSyncStart={handleCartSyncStart}
                        onCartSyncComplete={handleCartSyncComplete}
                      />
                    )}
                  </>
                ) : (
                  <>
                    {isSearching && (
                      <Flex justify="center" py="40px">
                        <Spinner size="lg" color="#007AFF" />
                      </Flex>
                    )}
                    {!isSearching && searchedVoucher && (
                      <Flex direction="column" gap="20px">
                        <VoucherListSection
                          vouchers={[searchedVoucher]}
                          statusFilter="all"
                          searchTerm=""
                          onVoucherApplied={(voucher) =>
                            voucher &&
                            debouncedHandleApply(voucher.id, voucher.code)
                          }
                          onVoucherClick={handleCardClick}
                          isVoucherDisabledByCombination={(voucher) =>
                            isVoucherDisabledByCombination(voucher.code)
                          }
                          onCartSyncStart={handleCartSyncStart}
                          onCartSyncComplete={handleCartSyncComplete}
                        />
                        <Button
                          variant="outline"
                          w="full"
                          onClick={handleBackToList}
                        >
                          Kembali ke Daftar Voucher
                        </Button>
                      </Flex>
                    )}
                  </>
                )}
              </Box>
            </>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default CartVoucherModal;
